import React, { useState, useEffect } from "react";
import { Box } from "@mui/system";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { AutoCompleteCustom } from "../../../../../components/Autocomplete/AutoCompleteCustom";
import CustomDatePicker from "../../../../../components/DatePicker/CustomDatePicker";
import TimeScheduler from "../../../../../components/TimeScheduler/TimeScheduler";
import { Button } from "../../../../../components/Buttons";
import {
  BASE,
  BRAND,
  GREY,
  HELP,
  INDIGO,
  PURPLE,
  BORDER,
  PRIMARY,
  SUCCESS,
} from "../../../../../utils/constants/colors";
import { fontWeight } from "../../../../../utils/constants/UI";
import { STRINGS } from "../../strings";
import {
  CAMPAIGN_CREATE_DAYS_LIST,
  initialTimings,
} from "../../../../../utils/constants";
import { ICON_LABELS } from "../../../../../utils/constants/UI";
import { getTimeStampForTimeZone } from "../../../../../utils/helpers";
import { TIME } from "../../../../../utils/constants/assets";
import Checkbox from "../../../../../components/Buttons/Checkbox/Checkbox";

import { IconComponent } from "../../../../../components/Icon";

const ScheduleTimingsStep = ({
  classes,
  timezoneList,
  timezone,
  setTimeZone,
  searchedTimezoneList,
  scheduleDate,
  setScheduleDate,
  editTiming,
  getTimings,
  handleGetData,
  setGetTimings,
  error,
  handleEligibleHours,
  isEligibleLoad,
  handleEditTimings,
  sequenceType,
  currentStep,
  totalSteps,
}) => {
  // ─── Live Clock ───
  const [liveTime, setLiveTime] = useState("");
  const selectedTz = Array.isArray(timezone) ? timezone[0] : timezone;

  useEffect(() => {
    if (!selectedTz) {
      setLiveTime("");
      return;
    }
    const tick = () => {
      try {
        const now = new Date();
        setLiveTime(
          now.toLocaleString("en-US", {
            timeZone: selectedTz,
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            weekday: "short",
            month: "short",
            day: "numeric",
          }),
        );
      } catch {
        setLiveTime("");
      }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [selectedTz]);

  // Snapshot timings when entering edit mode so Cancel can revert
  const [timingsSnapshot, setTimingsSnapshot] = useState(null);

  useEffect(() => {
    if (editTiming && !timingsSnapshot) {
      setTimingsSnapshot(JSON.parse(JSON.stringify(getTimings)));
    }
    if (!editTiming) {
      setTimingsSnapshot(null);
    }
  }, [editTiming]);

  const handleCancel = () => {
    if (timingsSnapshot) {
      setGetTimings(timingsSnapshot);
    }
    handleEditTimings();
  };

  if (sequenceType === "automated" || sequenceType === "tenantPrompt") {
    return (
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "420px",
          textAlign: "center",
          padding: "40px",
          borderRadius: "16px",
          overflow: "hidden",
          marginTop: "20px",
        }}
      >
        {/* ─── Clock Image + Ripple Waves ─── */}
        <Box
          sx={{
            position: "relative",
            width: "80px",
            height: "80px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "32px",
          }}
        >
          {/* ── Ripple Ring 1 ── */}
          <Box
            sx={{
              position: "absolute",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 200, 200, 0.7)",
              animation: "ripple1 2.8s ease-out infinite",
              "@keyframes ripple1": {
                "0%": { transform: "scale(1)", opacity: 1 },
                "100%": { transform: "scale(1.8)", opacity: 0 },
              },
            }}
          />
          {/* ── Ripple Ring 2 ── */}
          <Box
            sx={{
              position: "absolute",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 182, 182, 0.6)",
              animation: "ripple2 2.8s ease-out 0.7s infinite",
              "@keyframes ripple2": {
                "0%": { transform: "scale(1)", opacity: 1 },
                "100%": { transform: "scale(2)", opacity: 0 },
              },
            }}
          />
          {/* ── Ripple Ring 3 ── */}
          <Box
            sx={{
              position: "absolute",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "rgba(255, 236, 236, 0.5)",
              animation: "ripple3 2.8s ease-out 1.4s infinite",
              "@keyframes ripple3": {
                "0%": { transform: "scale(1)", opacity: 1 },
                "100%": { transform: "scale(2.2)", opacity: 0 },
              },
            }}
          />

          {/* ── Clock Image ── */}
          <img
            src={TIME}
            alt="Schedule"
            style={{
              width: "80px",
              height: "80px",
              position: "relative",
              zIndex: 2,
              filter: "brightness(0) invert(1)",
            }}
          />
        </Box>

        {/* ─── Heading + Subtext ─── */}
        <TypographyInline
          size={"lg"}
          fontWeight={fontWeight.BOLD}
          label="Schedule Timing After Creation"
          style={{
            fontSize: "22px",
            marginBottom: "8px",
            color: BRAND.SECONDARY,
            letterSpacing: "-0.3px",
            display: "block",
          }}
        />
        <TypographyInline
          size={"sm"}
          fontWeight={fontWeight.REGULAR}
          label="Sit back and relax, we're setting everything up for you."
          style={{
            fontSize: "14px",
            marginBottom: "20px",
            color: GREY.TERTIARY,
            display: "block",
          }}
        />
        {/* ─── Note Callout ─── */}
        <Box
          sx={{
            zIndex: 2,
            maxWidth: "520px",
            backgroundColor: PRIMARY.W_50,
            border: `1px solid ${PRIMARY.W_200}`,
            borderRadius: "12px",
            padding: "20px 24px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              lineHeight: "1.7",
              fontSize: "15px",
              color: GREY.SECONDARY,
            }}
          >
            <span
              style={{
                color: BRAND.PRIMARY,
                fontWeight: 700,
              }}
            >
              Please note:
            </span>{" "}
            We will now analyze your prospect data and generate personalized
            emails for your campaign. Once this is complete, you'll be able to
            schedule and launch it.
          </div>
        </Box>
      </Box>
    );
  }

  const activeDaysCount = getTimings.filter((t) => t.checked).length;
  const allSelected = getTimings.every((t) => t.checked);

  const handleSelectAll = (e) => {
    e?.stopPropagation?.();
    setGetTimings(getTimings.map((t) => ({ ...t, checked: !allSelected })));
  };

  return (
    <Box>
      {/* ── Header ── */}
      <div className={classes.div} style={stepStyles.header}>
        <div>
          <TypographyInline
            size={"md"}
            color={GREY.PRIMARY}
            fontWeight={fontWeight.SEMIBOLD}
            label={STRINGS.TIMINGS}
          />
          <TypographyInline
            size={"sm"}
            color={GREY.SECONDARY}
            fontWeight={fontWeight.REGULAR}
            label={
              currentStep
                ? `Step ${currentStep} of ${totalSteps}`
                : STRINGS.STEP45
            }
          />
        </div>
      </div>

      {/* ── Main Content ── */}
      <Box className={classes.containStep}>
        <Box sx={stepStyles.card}>
          {/* Timezone + Date row */}
          <Box sx={stepStyles.topRow}>
          <Box sx={stepStyles.field}>
            <Box sx={stepStyles.fieldHead}>
              <TypographyInline
                size={"xs"}
                color={GREY.QUATINARY}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.TIMEZONE}
              />
              {selectedTz && liveTime && (
                <Box sx={stepStyles.clockBadge}>
                  <Box sx={stepStyles.clockDot} />
                  <TypographyInline
                    size={"xs"}
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.MEDIUM}
                    label={liveTime}
                  />
                </Box>
              )}
            </Box>
            <AutoCompleteCustom
              items={timezoneList}
              valueState={timezone}
              idState={timezone}
              setIdState={setTimeZone}
              papermenuclass={classes.paperMenu}
              size={"sm36"}
              isSearchEnable={true}
              handleSearch={searchedTimezoneList}
            />
          </Box>
          <Box sx={stepStyles.fieldSm}>
            <Box sx={stepStyles.fieldHead}>
              <TypographyInline
                size={"xs"}
                color={GREY.QUATINARY}
                fontWeight={fontWeight.MEDIUM}
                label={STRINGS.SCHEDULE_DATE}
              />
            </Box>
            <CustomDatePicker
              minDate={
                new Date(getTimeStampForTimeZone(timezone?.[0] || timezone))
              }
              value={scheduleDate}
              onChange={(newValue) => {
                setScheduleDate(newValue?.$d?.toString());
              }}
              size={"sm36"}
            />
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={stepStyles.divider} />

        {/* ── Edit Mode ── */}
        {editTiming ? (
          <Box>
            {/* Sending Window header */}
            <Box sx={stepStyles.sectionHead}>
              <Box sx={stepStyles.sectionHeadLeft}>
                <TypographyInline
                  size="sm"
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.SEMIBOLD}
                  label="Sending Window"
                />
                {activeDaysCount > 0 && (
                  <Box sx={stepStyles.countChip}>
                    <TypographyInline
                      size="xs"
                      color={SUCCESS.W_600}
                      fontWeight={fontWeight.SEMIBOLD}
                      label={`${activeDaysCount} day${activeDaysCount !== 1 ? "s" : ""}`}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={stepStyles.actions}>
                <Box sx={stepStyles.actionLink} onClick={handleSelectAll}>
                  <Checkbox
                    noMargin
                    checked={allSelected}
                    onChange={() => {}}
                    style={{ pointerEvents: "none" }}
                  />
                  <TypographyInline
                    size="xs"
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.MEDIUM}
                    label={allSelected ? "Deselect all" : "Select all"}
                  />
                </Box>
                <Box sx={stepStyles.actionSep} />
                <Box
                  sx={stepStyles.actionLink}
                  onClick={() => setGetTimings([...initialTimings])}
                >
                  <IconComponent
                    iconLabel={ICON_LABELS.RESTART_ALT}
                    color={GREY.TERTIARY}
                    fontSize="13px"
                  />
                  <TypographyInline
                    size="xs"
                    color={GREY.SECONDARY}
                    fontWeight={fontWeight.MEDIUM}
                    label="Reset"
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={stepStyles.scrollArea}>
              <TimeScheduler
                payload={getTimings}
                getData={(e) => handleGetData(e)}
              />
            </Box>

            {/* Action buttons */}
            <Box sx={stepStyles.buttonBar}>
              <Box sx={{ width: "auto" }}>
                <Button
                  label="Cancel"
                  size={"sm36"}
                  variant={"ghost"}
                  onClick={handleCancel}
                />
              </Box>
              <Box sx={{ width: "auto" }}>
                <Button
                  LeftIcon={ICON_LABELS.SAVE}
                  label={STRINGS.SAVE}
                  size={"sm36"}
                  variant={"primary"}
                  disabled={error}
                  onClick={handleEligibleHours}
                  isLoading={isEligibleLoad}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          /* ── Read-Only Summary ── */
          <Box>
            {/* Sending Window header (read-only) */}
            <Box sx={{ ...stepStyles.sectionHead, marginBottom: "4px" }}>
              <Box sx={stepStyles.sectionHeadLeft}>
                <TypographyInline
                  size="sm"
                  color={GREY.PRIMARY}
                  fontWeight={fontWeight.SEMIBOLD}
                  label="Sending Window"
                />
                {activeDaysCount > 0 && (
                  <Box sx={stepStyles.countChip}>
                    <TypographyInline
                      size="xs"
                      color={SUCCESS.W_600}
                      fontWeight={fontWeight.SEMIBOLD}
                      label={`${activeDaysCount} day${activeDaysCount !== 1 ? "s" : ""}`}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            <Box sx={{ ...stepStyles.scrollArea, display: "flex", flexDirection: "column" }}>
              {CAMPAIGN_CREATE_DAYS_LIST?.map((eachDay, index) => {
                const isChecked = getTimings?.filter(
                  (timing) => timing.day === index,
                )?.[0]?.checked;

                if (!isChecked) return null;

                const timingIndex = getTimings?.findIndex(
                  (obj) => obj.day === index,
                );
                const timing = getTimings?.[timingIndex];

                return (
                  <Box
                    key={eachDay.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "14px",
                      padding: "12px 0",
                      borderBottom: `1px solid ${BORDER.LIGHT}`,
                      "&:last-child": { borderBottom: "none" },
                    }}
                  >
                    <Box sx={{ minWidth: "96px", flexShrink: 0 }}>
                      <TypographyInline
                        size={"sm"}
                        color={GREY.PRIMARY}
                        fontWeight={fontWeight.SEMIBOLD}
                        isCapitalise={true}
                        label={eachDay.day}
                      />
                    </Box>

                    <TypographyInline
                      size={"sm"}
                      color={GREY.TERTIARY}
                      fontWeight={fontWeight.REGULAR}
                      label="from"
                    />

                    <Box sx={stepStyles.timeBadge}>
                      <TypographyInline
                        size={"sm"}
                        color={GREY.SECONDARY}
                        fontWeight={fontWeight.MEDIUM}
                        label={`${timing?.startTime?.toString()}:${String(timing?.startMinute).padStart(2, "0")} ${timing?.startType}`}
                      />
                    </Box>

                    <TypographyInline
                      size={"sm"}
                      color={GREY.TERTIARY}
                      fontWeight={fontWeight.REGULAR}
                      label="to"
                    />

                    <Box sx={stepStyles.timeBadge}>
                      <TypographyInline
                        size={"sm"}
                        color={GREY.SECONDARY}
                        fontWeight={fontWeight.MEDIUM}
                        label={`${timing?.endTime?.toString()}:${String(timing?.endMinute).padStart(2, "0")} ${timing?.endType}`}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Edit button */}
            <Box sx={stepStyles.buttonBar}>
              <Box sx={{ width: "auto" }}>
                <Button
                  label={STRINGS.EDIT}
                  size={"sm36"}
                  variant={"primary"}
                  onClick={handleEditTimings}
                />
              </Box>
            </Box>
          </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
};

// Styles matching ScheduleAutomatedCampaignModal left panel
const stepStyles = {
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  card: {
    background: "#FFFFFF",
    borderRadius: "10px",
    border: `1px solid ${BORDER.DEFAULT}`,
    padding: "18px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  scrollArea: {
    maxHeight: "45vh",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": { width: "3px" },
    "&::-webkit-scrollbar-thumb": {
      background: GREY.DISABLED,
      borderRadius: "3px",
    },
  },
  buttonBar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "8px",
    paddingTop: "14px",
    marginTop: "8px",
  },
  topRow: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-end",
  },
  field: {
    flex: 1.3,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldSm: {
    flex: 0.9,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "22px",
  },
  clockBadge: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "1px 8px",
    borderRadius: "10px",
    background: "#F5F5F5",
    border: `1px solid ${BORDER.DEFAULT}`,
  },
  clockDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    background: SUCCESS.W_500,
  },
  divider: {
    height: "1px",
    background: BORDER.LIGHT,
    margin: "14px 0",
  },
  sectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  sectionHeadLeft: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  countChip: {
    padding: "1px 8px",
    borderRadius: "10px",
    background: SUCCESS.W_50,
    border: `1px solid ${SUCCESS.W_100}`,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
  },
  actionLink: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    cursor: "pointer",
    padding: "3px 7px",
    borderRadius: "6px",
    transition: "background 0.15s",
    "&:hover": { background: "#F5F5F5" },
  },
  actionSep: {
    width: "1px",
    height: "12px",
    background: BORDER.DEFAULT,
    margin: "0 2px",
  },
  timeBadge: {
    flex: 1,
    padding: "6px 14px",
    background: "#F8F8F8",
    border: `1px solid ${BORDER.DEFAULT}`,
    borderRadius: "6px",
  },
};

export default React.memo(ScheduleTimingsStep);
